import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { BOOKING_URL } from "@/config/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { label: "Home", path: "/" },
    { label: "About", path: "/about" },
    { label: "Services", path: "/services" },
    { label: "Blog", path: "/blog" },
    { label: "Contact", path: "/contact" },
  ];

  const isActive = (path: string) => location.pathname === path;
  const isHome = location.pathname === "/";
  const isContact = location.pathname === "/contact";
  const useWhiteLinks = (isHome || isContact) && !scrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-28 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <img
            src="/images/logo.jpg"
            alt="Clarity Point Counseling Logo"
            className="h-20 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                isActive(link.path)
                  ? useWhiteLinks ? "text-white font-semibold" : "text-[#418b86] font-semibold"
                  : useWhiteLinks ? "text-white/90 hover:text-white" : "text-[#3a3a3a] hover:text-[#418b86]"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* CTA Button */}
        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden md:flex items-center gap-2 bg-[#418b86] hover:bg-[#4db8b1] text-white text-sm font-medium px-5 py-2.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          Schedule a Consultation
          <i className="ri-arrow-right-line text-sm"></i>
        </a>

        {/* Mobile Hamburger */}
        <button
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full transition-colors cursor-pointer ${useWhiteLinks ? "text-white" : "text-[#3a3a3a]"}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white/98 backdrop-blur-md border-t border-[#d0ecea] px-6 py-6 flex flex-col gap-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-base font-medium py-2 cursor-pointer ${
                isActive(link.path) ? "text-[#418b86]" : "text-[#4a4a4a]"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 bg-[#418b86] text-white text-sm font-medium px-5 py-3 rounded-full text-center cursor-pointer whitespace-nowrap"
          >
            Schedule a Consultation
          </a>
        </div>
      )}
    </header>
  );
}
